import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "../ThemeContext";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../Config/config";
import styled from "styled-components";
import { ChevronLeft, ChevronRight, MapPin } from "lucide-react";

const Hotels = ({ searchLocation, selectedLocationType }) => {
  const { themes, currentTheme } = useTheme();
  const theme = themes[currentTheme];
  const navigate = useNavigate();
  const [hotels, setHotels] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        setIsLoading(true);
        const hotelsCollection = collection(db, "hotels");
        const hotelsSnapshot = await getDocs(hotelsCollection);
        const hotelsList = hotelsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setHotels(hotelsList);
        setFilteredHotels(hotelsList);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching hotels:", error);
        setError("Failed to fetch hotels. Please try again later.");
        setIsLoading(false);
      }
    };

    fetchHotels();
  }, []);

  useEffect(() => {
    if (searchLocation) {
      const filtered = hotels.filter((hotel) => {
        const locationWords = hotel.hotelLocation.toLowerCase().split(/\W+/).map(word => word.trim()).filter(word => word);
        const locationTypeWords = hotel.hotelLocationType.toLowerCase().split(/\W+/).map(word => word.trim()).filter(word => word);
        const searchWords = searchLocation.toLowerCase().split(/\W+/).map(word => word.trim()).filter(word => word);
        return searchWords.some(word => 
          locationWords.some(locWord => locWord.startsWith(word)) ||
          locationTypeWords.some(typeWord => typeWord.startsWith(word))
        );
      });
      setFilteredHotels(filtered);
    } else if (selectedLocationType) {
      const filtered = hotels.filter(
        (hotel) =>
          hotel.hotelLocationType &&
          hotel.hotelLocationType.toLowerCase() ===
            selectedLocationType.toLowerCase()
      );
      setFilteredHotels(filtered);
    } else {
      setFilteredHotels(hotels);
    }
  }, [searchLocation, selectedLocationType, hotels]);

  const handleHotelClick = (hotelId) => {
    navigate(`/hotel/${hotelId}`);
  };

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount =
        direction === "left" ? -container.offsetWidth : container.offsetWidth;
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  if (error) return <ErrorMessage>{error}</ErrorMessage>;

  if (isLoading) {
    return (
      <Container backgroundColor={theme.bgColor}>
        <Title color={theme.textColor}>HOTELS</Title>
        <LoadingMessage color={theme.textColor}>Loading hotels...</LoadingMessage>
      </Container>
    );
  }

  if (filteredHotels.length === 0) {
    return (
      <Container backgroundColor={theme.bgColor}>
        <Title color={theme.textColor}>HOTELS</Title>
        <NoHotelsMessage color={theme.textColor}>
          No hotels found for the current selection.
        </NoHotelsMessage>
      </Container>
    );
  }

  return (
    <Container backgroundColor={theme.bgColor}>
      <Title color={theme.textColor}>HOTELS</Title>
      <ScrollContainer>
        <ScrollButton onClick={() => scroll("left")} position="left">
          <ChevronLeft size={24} />
        </ScrollButton>
        <HotelGrid ref={scrollContainerRef}>
          {filteredHotels.map((hotel) => (
            <HotelCard
              key={hotel.id}
              onClick={() => handleHotelClick(hotel.id)}
            >
              <CardImageWrapper>
                <CardImage
                  src={
                    hotel.imagesArray?.[0] ||
                    "https://via.placeholder.com/300x240"
                  }
                  alt={hotel.hotelName}
                  loading="lazy"
                  decoding="async"
                />
              </CardImageWrapper>
              <CardContent>
                <CardTitle>{hotel.hotelName}</CardTitle>
                <LocationContainer>
                  <MapPin size={16} />
                  <CardLocation>{hotel.hotelLocation}</CardLocation>
                </LocationContainer>
              </CardContent>
            </HotelCard>
          ))}
        </HotelGrid>
        <ScrollButton onClick={() => scroll("right")} position="right">
          <ChevronRight size={24} />
        </ScrollButton>
      </ScrollContainer>
    </Container>
  );
};

// Styled components (unchanged)
const Container = styled.div`
  padding: 20px;
  background-color: ${(props) => props.backgroundColor};
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const Title = styled.h2`
  font-size: 10vh;
  color: ${(props) => props.color};
  text-align: center;
  margin-bottom: 20px;
  @media (max-width: 768px) {
    font-size: 6vh;
  }
`;

const LoadingMessage = styled.div`
  color: ${(props) => props.color};
  text-align: center;
  font-size: 1.2rem;
  margin-top: 20px;
`;

const ScrollContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const HotelGrid = styled.div`
  display: flex;
  gap: 18px;
  overflow-x: auto;
  padding: 20px;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  @media (max-width: 768px) {
    display: grid;
    width: calc(100% - 20px);
    margin: 0 auto;
    grid-template-columns: calc(50% - 10px) calc(50% - 10px);
    gap: 20px;
    padding: 0;
    overflow-x: visible;
  }
`;

const HotelCard = styled.div`
  flex: 0 0 30vh;
  scroll-snap-align: start;
  cursor: pointer;
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
    transform: translateY(-4px);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    flex: none;
    min-height: 225px;
  }
`;

const CardImageWrapper = styled.div`
  width: 100%;
  padding-top: 56.25%;
  position: relative;
`;

const CardImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: 5px solid white;
  border-radius: 10px;
`;

const CardContent = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  
  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const CardTitle = styled.h3`
  font-size: 0.8rem;
  margin: 0 0 8px 0;
  color: #333;
  line-height: 1.3;
  text-align: center;
  word-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 8px;
  }
`;

const LocationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #666;
  margin-top: auto;
  padding-top: 8px;
`;

const CardLocation = styled.p`
  margin: 0;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 4px;
  word-wrap: break-word;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const ErrorMessage = styled.div`
  color: red;
  text-align: center;
  font-size: 1.2rem;
  margin-top: 20px;
`;

const NoHotelsMessage = styled.div`
  color: ${(props) => props.color};
  text-align: center;
  font-size: 1.2rem;
  margin-top: 20px;
`;

const ScrollButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(255, 255, 255, 0.7);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 10;
  ${(props) => props.position}: 10px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.9);
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

export default Hotels;